import * as React from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { Button } from './button.js';
import { DisabledHintTooltip } from './disabled-hint-tooltip.js';
import { Popover, PopoverContent, PopoverTrigger } from './popover.js';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command.js';

export interface SearchableSelectFieldOption {
  value: string;
  label: string;
}

export interface SearchableSelectFieldProps {
  id?: string;
  options: SearchableSelectFieldOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shown on the trigger when no option is selected. */
  placeholder?: string;
  /** Search box placeholder. Defaults to `placeholder` when omitted. */
  searchPlaceholder?: string;
  /** Shown when the search yields no matches. Defaults to `placeholder` when omitted. */
  emptyText?: string;
  /** When true, prepends a clear/empty option to the dropdown list. */
  allowEmpty?: boolean;
  /** Label shown for the clear option when allowEmpty is true. Defaults to '—'. */
  emptyLabel?: string;
  /**
   * Optional action row rendered before the option list (e.g. "Create new client") — always
   * visible regardless of the search text, including when it yields zero option matches.
   * Use for a create-inline affordance instead of a disconnected button below the field.
   */
  action?: {
    label: string;
    onSelect: () => void;
    disabled?: boolean;
    /** Tooltip shown on hover while `disabled`. */
    hint?: string;
    testId?: string;
  };
  disabled?: boolean;
  error?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

const EMPTY_SENTINEL = '__empty__';
const ACTION_SENTINEL = '__action__';

/**
 * Case-insensitive substring match, close enough to cmdk's default fuzzy
 * scoring for the short entity-name lists this field is used for. Only
 * takes over from cmdk's built-in filter when `action` is set, so it never
 * changes behavior for the many callers that don't use the action row.
 */
function filterWithAlwaysVisibleAction(value: string, search: string): number {
  if (value === ACTION_SENTINEL) return 1;
  return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
}

interface SearchableSelectFieldOptionItemProps {
  optionValue: string;
  label: string;
  isSelected: boolean;
  testId?: string;
  onSelect: (value: string) => void;
}

function SearchableSelectFieldOptionItem({
  optionValue,
  label,
  isSelected,
  testId,
  onSelect,
}: SearchableSelectFieldOptionItemProps) {
  return (
    <CommandItem data-testid={testId} value={label} onSelect={() => onSelect(optionValue)}>
      <Check className={cn('h-3.5 w-3.5', isSelected ? 'opacity-100' : 'opacity-0')} />
      <span className="truncate">{label}</span>
    </CommandItem>
  );
}

interface SearchableSelectFieldOptionListProps {
  options: SearchableSelectFieldOption[];
  value: string;
  allowEmpty: boolean;
  emptyLabel: string;
  testId?: string;
  onSelect: (value: string) => void;
}

function SearchableSelectFieldOptionList({
  options,
  value,
  allowEmpty,
  emptyLabel,
  testId,
  onSelect,
}: SearchableSelectFieldOptionListProps) {
  return (
    <CommandGroup>
      {allowEmpty ? (
        <SearchableSelectFieldOptionItem
          optionValue={EMPTY_SENTINEL}
          label={emptyLabel}
          isSelected={value === ''}
          testId={testId ? `${testId}-option-empty` : undefined}
          onSelect={onSelect}
        />
      ) : null}
      {options.map((option) => (
        <SearchableSelectFieldOptionItem
          key={option.value}
          optionValue={option.value}
          label={option.label}
          isSelected={option.value === value}
          testId={testId ? `${testId}-option-${option.value}` : undefined}
          onSelect={onSelect}
        />
      ))}
    </CommandGroup>
  );
}

interface SearchableSelectFieldActionItemProps {
  action?: SearchableSelectFieldProps['action'];
  onSelect: () => void;
}

/** Renders nothing when `action` is unset — keeps the ternary out of the parent's complexity. */
function SearchableSelectFieldActionItem({
  action,
  onSelect,
}: SearchableSelectFieldActionItemProps) {
  if (!action) return null;
  const { label, disabled = false, hint, testId } = action;
  return (
    <CommandGroup className="border-t border-border/40 pt-1">
      <DisabledHintTooltip disabled={disabled} hint={hint}>
        <CommandItem
          value={ACTION_SENTINEL}
          disabled={disabled}
          data-testid={testId}
          onSelect={onSelect}
          className="text-primary data-[disabled=true]:text-muted-foreground"
        >
          <Plus className="h-3.5 w-3.5" />
          <span className="truncate">{label}</span>
        </CommandItem>
      </DisabledHintTooltip>
    </CommandGroup>
  );
}

/** Extracted so the parent component doesn't carry this branch in its own complexity count. */
function resolveCommandFilter(action: SearchableSelectFieldProps['action']) {
  return action ? filterWithAlwaysVisibleAction : undefined;
}

interface SearchableSelectFieldTriggerProps {
  id?: string;
  open: boolean;
  disabled: boolean;
  error: boolean;
  ariaLabel?: string;
  testId?: string;
  className?: string;
  label?: string;
}

// Radix's `PopoverTrigger asChild` clones this element via Slot, injecting its own onClick
// and ref — both MUST reach the underlying <button>, so this forwards the ref and spreads any
// extra props (...rest) instead of only reading its own named props.
const SearchableSelectFieldTrigger = React.forwardRef<
  HTMLButtonElement,
  SearchableSelectFieldTriggerProps & React.ComponentPropsWithoutRef<'button'>
>(({ id, open, disabled, error, ariaLabel, testId, className, label, ...rest }, ref) => (
  <Button
    ref={ref}
    id={id}
    type="button"
    variant="outline"
    role="combobox"
    aria-expanded={open}
    aria-label={ariaLabel}
    disabled={disabled}
    data-testid={testId}
    className={cn(
      'h-9 w-full justify-between text-[13px] font-normal',
      error && 'border-destructive focus:ring-destructive/30',
      className,
    )}
    {...rest}
  >
    <span className={cn('truncate', label == null && 'text-muted-foreground')}>{label}</span>
    <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
  </Button>
));
SearchableSelectFieldTrigger.displayName = 'SearchableSelectFieldTrigger';

/**
 * Single-select trigger button + searchable list popover. The searchable
 * counterpart to `MultiSelectCombobox` for single-value pickers — built from
 * the same shared `Popover` + `Command` primitives. Prefer this over the
 * non-searchable `SelectField` for any closed-option list, since a plain
 * Radix `<Select>` has no way to filter long option lists.
 */
export function SearchableSelectField({
  id,
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  allowEmpty = false,
  emptyLabel = '—',
  disabled = false,
  error = false,
  className,
  action,
  'aria-label': ariaLabel,
  'data-testid': testId,
}: SearchableSelectFieldProps) {
  const [open, setOpen] = React.useState(false);

  const selectedLabel = options.find((option) => option.value === value)?.label;

  function handleSelect(next: string) {
    if (next === ACTION_SENTINEL) return;
    onChange(next === EMPTY_SENTINEL ? '' : next);
    setOpen(false);
  }

  function handleActionSelect() {
    setOpen(false);
    action?.onSelect();
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SearchableSelectFieldTrigger
          id={id}
          open={open}
          disabled={disabled}
          error={error}
          ariaLabel={ariaLabel}
          testId={testId}
          className={className}
          label={selectedLabel ?? placeholder}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command filter={resolveCommandFilter(action)}>
          <CommandInput
            data-testid={testId ? `${testId}-search` : undefined}
            placeholder={searchPlaceholder ?? placeholder}
          />
          <CommandList>
            <CommandEmpty>{emptyText ?? placeholder}</CommandEmpty>
            <SearchableSelectFieldActionItem action={action} onSelect={handleActionSelect} />
            <SearchableSelectFieldOptionList
              options={options}
              value={value}
              allowEmpty={allowEmpty}
              emptyLabel={emptyLabel}
              testId={testId}
              onSelect={handleSelect}
            />
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
